import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Link as LinkIcon, 
  Github, 
  Mail, 
  Phone, 
  Briefcase, 
  GraduationCap, 
  Star, 
  CheckCircle, 
  XCircle,
  Save,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import InterviewForm from '@/components/interviews/interview-form';

interface CandidateProfileViewProps {
  profileId: string;
  jobId: string;
  applicationId?: string;
  onBack?: () => void;
}

export function CandidateProfileView({ 
  profileId, 
  jobId, 
  applicationId, 
  onBack 
}: CandidateProfileViewProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [experiences, setExperiences] = useState<any[]>([]);
  const [education, setEducation] = useState<any[]>([]);
  const [job, setJob] = useState<any>(null);
  const [application, setApplication] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [interviewFormOpen, setInterviewFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [matchAnalysis, setMatchAnalysis] = useState<any>(null);
  const [analyzingMatch, setAnalyzingMatch] = useState(false);

  useEffect(() => {
    if (profileId && jobId) {
      fetchData();
    }
  }, [profileId, jobId, applicationId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          experiences (
            id,
            position,
            company_name,
            start_date,
            end_date,
            current,
            description,
            location
          ),
          education (
            id,
            institution,
            degree,
            field_of_study,
            start_date,
            end_date,
            current,
            description,
            location
          )
        `)
        .eq('id', profileId)
        .single();

      if (profileError) throw profileError;
      
      setProfile(profileData);
      setExperiences(profileData.experiences || []);
      setEducation(profileData.education || []);

      // Fetch job details
      const { data: jobData, error: jobError } = await supabase
        .from('recruiting_projects')
        .select('*')
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;
      setJob(jobData);

      // Fetch application if available
      if (applicationId) {
        const { data: appData, error: appError } = await supabase
          .from('applications')
          .select('*, notes:candidate_evaluations(id, notes, created_at)')
          .eq('id', applicationId)
          .single();

        if (!appError && appData) {
          setApplication(appData);
          // Set notes from the most recent evaluation if available
          if (appData.notes && appData.notes.length > 0) {
            // Sort by created_at descending and get the most recent notes
            const sortedNotes = [...appData.notes].sort((a, b) => 
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setNotes(sortedNotes[0].notes || '');
          }
        }
      }

      // Fetch match analysis if available
      fetchMatchAnalysis(profileId, jobId);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load candidate data'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchAnalysis = async (candidateId: string, jobId: string) => {
    setAnalyzingMatch(true);
    try {
      // First check if we already have a match in the database
      const { data: matchData, error: matchError } = await supabase
        .from('job_matches')
        .select('*')
        .eq('profile_id', candidateId)
        .eq('job_id', jobId)
        .maybeSingle();

      if (!matchError && matchData) {
        setMatchAnalysis(matchData);
      } else {
        // If no match exists, we could generate one on the fly
        // This would typically call an API endpoint or function
        // For now, we'll create a simple placeholder analysis
        setMatchAnalysis({
          overall_score: 75,
          skills_score: 80,
          experience_score: 70,
          location_score: 90,
          education_score: 65,
          match_details: {
            matched_skills: job?.skills?.filter((skill: string) => 
              Math.random() > 0.3
            ) || [],
            missing_skills: job?.skills?.filter((skill: string) => 
              Math.random() <= 0.3
            ) || [],
            experience_years: Math.floor(Math.random() * 10) + 1,
            location_match: Math.random() > 0.5,
            education_match: Math.random() > 0.3
          }
        });
      }
    } catch (error) {
      console.error('Error fetching match analysis:', error);
    } finally {
      setAnalyzingMatch(false);
    }
  };

  const saveNotes = async () => {
    if (!user || !application) return;
    
    setSavingNotes(true);
    try {
      // Check if there's an existing evaluation
      const { data: existingEval, error: checkError } = await supabase
        .from('candidate_evaluations')
        .select('id')
        .eq('profile_id', profileId)
        .eq('application_id', application.id)
        .eq('evaluator_id', user.id)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingEval) {
        // Update existing evaluation
        const { error: updateError } = await supabase
          .from('candidate_evaluations')
          .update({ 
            notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingEval.id);

        if (updateError) throw updateError;
      } else {
        // Create new evaluation
        const { error: insertError } = await supabase
          .from('candidate_evaluations')
          .insert([{
            profile_id: profileId,
            evaluator_id: user.id,
            application_id: application.id,
            notes,
            skills_rating: 5, // Default values
            experience_rating: 5,
            education_rating: 5,
            communication_rating: 5,
            overall_rating: 5,
            status: 'completed',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (insertError) throw insertError;
      }

      toast({
        title: 'Success',
        description: 'Notes saved successfully'
      });
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save notes'
      });
    } finally {
      setSavingNotes(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Present';
    return format(new Date(dateString), 'MMM yyyy');
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold mb-2">Candidate Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The requested candidate profile could not be found.
        </p>
        <Button onClick={onBack || (() => navigate(-1))}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack || (() => navigate(-1))}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Candidate Profile</h1>
          <p className="text-muted-foreground">
            Viewing {profile.full_name} for {job?.title}
          </p>
        </div>
      </div>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="match">Job Match</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          {/* Basic Info Card */}
          <Card>
            <CardHeader className="flex flex-row items-start gap-4 pb-2">
              <Avatar className="h-16 w-16">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback>{profile.full_name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <CardTitle>{profile.full_name}</CardTitle>
                <CardDescription>{profile.title}</CardDescription>
                
                <div className="flex flex-wrap gap-4 mt-2 text-sm">
                  {profile.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{profile.location}</span>
                    </div>
                  )}
                  {profile.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a href={`mailto:${profile.email}`} className="hover:text-primary">
                        {profile.email}
                      </a>
                    </div>
                  )}
                  {profile.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{profile.phone}</span>
                    </div>
                  )}
                  {profile.github && (
                    <div className="flex items-center gap-1">
                      <Github className="h-4 w-4 text-muted-foreground" />
                      <a href={profile.github} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                        {profile.github.replace(/^https?:\/\/(www\.)?github\.com\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setInterviewFormOpen(true)}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Interview
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {profile.summary ? (
                <div>
                  <h3 className="font-medium mb-2">Professional Summary</h3>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {profile.summary}
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground italic">No summary provided</p>
              )}
            </CardContent>
          </Card>

          {/* Experience Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Work Experience</CardTitle>
            </CardHeader>
            <CardContent>
              {experiences.length > 0 ? (
                <div className="space-y-6">
                  {experiences.map((exp) => (
                    <div key={exp.id} className="border-b last:border-0 pb-4 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{exp.position}</h4>
                          <p className="text-sm text-muted-foreground">{exp.company_name}</p>
                        </div>
                        <div className="text-sm text-right">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{formatDate(exp.start_date)} - {exp.current ? 'Present' : formatDate(exp.end_date)}</span>
                          </div>
                          {exp.location && (
                            <div className="flex items-center gap-1 mt-1">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{exp.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {exp.description && (
                        <p className="mt-2 text-sm whitespace-pre-line">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No work experience listed</p>
              )}
            </CardContent>
          </Card>

          {/* Education Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Education</CardTitle>
            </CardHeader>
            <CardContent>
              {education.length > 0 ? (
                <div className="space-y-6">
                  {education.map((edu) => (
                    <div key={edu.id} className="border-b last:border-0 pb-4 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{edu.degree}</h4>
                          <p className="text-sm text-muted-foreground">{edu.institution}</p>
                          {edu.field_of_study && (
                            <p className="text-sm">{edu.field_of_study}</p>
                          )}
                        </div>
                        <div className="text-sm text-right">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{formatDate(edu.start_date)} - {edu.current ? 'Present' : formatDate(edu.end_date)}</span>
                          </div>
                          {edu.location && (
                            <div className="flex items-center gap-1 mt-1">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{edu.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {edu.description && (
                        <p className="mt-2 text-sm whitespace-pre-line">{edu.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No education listed</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Job Match Tab */}
        <TabsContent value="match" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Job Match Analysis</CardTitle>
              <CardDescription>
                Comparing candidate qualifications with job requirements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {analyzingMatch ? (
                <div className="flex justify-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Analyzing match...</p>
                  </div>
                </div>
              ) : matchAnalysis ? (
                <div className="space-y-6">
                  {/* Overall Match Score */}
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Overall Match</h3>
                    <Badge className={`${
                      matchAnalysis.overall_score >= 80 ? 'bg-green-100 text-green-800' :
                      matchAnalysis.overall_score >= 60 ? 'bg-blue-100 text-blue-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {matchAnalysis.overall_score}% Match
                    </Badge>
                  </div>

                  {/* Category Scores */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Skills Match</span>
                        <span className="text-sm font-medium">{matchAnalysis.skills_score}%</span>
                      </div>
                      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${matchAnalysis.skills_score}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Experience Match</span>
                        <span className="text-sm font-medium">{matchAnalysis.experience_score}%</span>
                      </div>
                      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${matchAnalysis.experience_score}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Location Match</span>
                        <span className="text-sm font-medium">{matchAnalysis.location_score}%</span>
                      </div>
                      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${matchAnalysis.location_score}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Education Match</span>
                        <span className="text-sm font-medium">{matchAnalysis.education_score}%</span>
                      </div>
                      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${matchAnalysis.education_score}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Skills Breakdown */}
                  <div className="pt-4 border-t">
                    <h3 className="font-medium mb-3">Skills Analysis</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Matched Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {matchAnalysis.match_details?.matched_skills?.map((skill: string, index: number) => (
                            <Badge key={index} variant="secondary" className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              {skill}
                            </Badge>
                          ))}
                          {(!matchAnalysis.match_details?.matched_skills || 
                            matchAnalysis.match_details.matched_skills.length === 0) && (
                            <p className="text-sm text-muted-foreground">No matched skills found</p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium mb-2">Missing Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {matchAnalysis.match_details?.missing_skills?.map((skill: string, index: number) => (
                            <Badge key={index} variant="outline" className="flex items-center gap-1">
                              <XCircle className="h-3 w-3 text-destructive" />
                              {skill}
                            </Badge>
                          ))}
                          {(!matchAnalysis.match_details?.missing_skills || 
                            matchAnalysis.match_details.missing_skills.length === 0) && (
                            <p className="text-sm text-muted-foreground">No missing skills identified</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Other Match Details */}
                  <div className="pt-4 border-t">
                    <h3 className="font-medium mb-3">Additional Insights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="text-sm font-medium mb-2">Experience</h4>
                        <p className="text-sm">
                          {matchAnalysis.match_details?.experience_years 
                            ? `${matchAnalysis.match_details.experience_years} years of relevant experience` 
                            : 'Experience information not available'}
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="text-sm font-medium mb-2">Location</h4>
                        <p className="text-sm flex items-center gap-2">
                          {matchAnalysis.match_details?.location_match 
                            ? <CheckCircle className="h-4 w-4 text-green-500" /> 
                            : <XCircle className="h-4 w-4 text-destructive" />}
                          {matchAnalysis.match_details?.location_match 
                            ? 'Location matches job requirements' 
                            : 'Location does not match requirements'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No match analysis available</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => fetchMatchAnalysis(profileId, jobId)}
                  >
                    Generate Match Analysis
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Candidate Notes</CardTitle>
              <CardDescription>
                Add private notes about this candidate
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add your notes about this candidate here..."
                className="min-h-[200px]"
              />
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button 
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Notes
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Interview Form */}
      <InterviewForm
        open={interviewFormOpen}
        onOpenChange={setInterviewFormOpen}
        job={job}
        application={application}
        onSuccess={() => {
          setInterviewFormOpen(false);
          toast({
            title: 'Success',
            description: 'Interview scheduled successfully',
          });
        }}
      />
    </div>
  );
}